/**
 * Reimpressió d'etiquetes de lot (Existències · OBR-178): les formes que creuen la frontera.
 *
 *  · {@link LotLabelReprintInput}: el que el BackOffice i l'App envien a l'RPC `record_lot_label_reprint` (mai el número de lot ni la
 *    caducitat: els llegeix el servidor del propi lot) i {@link lotLabelReprintRpcArgs}, amb els noms exactes de la funció (`p_…`).
 *  · {@link LotLabelReprintRow}: una fila de l'historial d'UN lot (`lot_label_reprint_history`).
 *  · {@link StockLabelLotRow}: una línia (lot + lloc) amb existències de `stock_lots_for_labels`, la còpia local de l'App.
 *
 * Tots els esquemes són `.strict()`: una clau que no coneixem és un error, no un valor que es perd en silenci.
 */

import { z } from 'zod';
import {
  LOT_LABEL_REPRINT_NOTE_MAX,
  LOT_LABEL_REPRINT_ORIGINS,
  LOT_LABEL_REPRINT_REASONS,
  MAX_LABEL_COPIES,
  MIN_LABEL_COPIES,
} from '../domain/lotLabel.js';
import { dateString, nonEmptyString, timestamptz, uuid } from './common.js';

/**
 * Registrar una reimpressió. Exactament UN lot (de proveïdor O de producció); el lloc és opcional (tot null = «sense lloc»); el
 * motiu és obligatori i, si és «altres», la nota també. `client_uuid` és la clau d'idempotència: UN uuid per obertura del diàleg (o
 * per operació de la cua de planta), reutilitzat en cada reintent; un reintent torna la MATEIXA fila i no n'escriu una segona.
 * `printed_at` (opcional) és l'instant de l'aparell si l'etiqueta va sortir sense connexió; el servidor mai l'accepta en el futur.
 */
export const LotLabelReprintInput = z
  .object({
    article_id: uuid,
    supplier_lot_id: uuid.nullable(),
    finished_lot_id: uuid.nullable(),
    zone_id: uuid.nullable(),
    section_id: uuid.nullable(),
    location_id: uuid.nullable(),
    copies: z.number().int().min(MIN_LABEL_COPIES).max(MAX_LABEL_COPIES),
    reason: z.enum(LOT_LABEL_REPRINT_REASONS),
    note: z.string().trim().max(LOT_LABEL_REPRINT_NOTE_MAX).nullable().optional(),
    origin: z.enum(LOT_LABEL_REPRINT_ORIGINS),
    client_uuid: uuid,
    printed_at: timestamptz.nullable().optional(),
    /**
     * La caducitat que PORTA l'etiqueta que s'ha imprès (`YYYY-MM-DD`), o null si no en portava cap; absent = «no ho dic»
     * (el servidor desa la del lot). Qui imprimeix diu què ha sortit al paper: el BackOffice, la que ha llegit del lot just abans
     * de generar el PDF; l'App, la de la còpia local. El servidor la desa tal com és i, a part, la que el lot tenia en registrar
     * (`lot_expiry_date`): si no coincideixen, el registre ho ensenya.
     */
    label_expiry_date: dateString.nullable().optional(),
  })
  .strict()
  .refine((m) => (m.supplier_lot_id === null) !== (m.finished_lot_id === null), {
    message: 'a reprint belongs to exactly one lot',
    path: ['finished_lot_id'],
  })
  .refine((m) => m.reason !== 'other' || (m.note ?? '').trim() !== '', {
    message: 'the reason «other» needs a note',
    path: ['note'],
  });
export type LotLabelReprintInput = z.infer<typeof LotLabelReprintInput>;

/** Els arguments de l'RPC `record_lot_label_reprint`, amb els noms exactes de la funció (`p_…`), en l'ordre de la funció. */
export function lotLabelReprintRpcArgs(input: LotLabelReprintInput): Record<string, unknown> {
  const note = (input.note ?? '').trim();
  return {
    p_article_id: input.article_id,
    p_supplier_lot_id: input.supplier_lot_id,
    p_finished_lot_id: input.finished_lot_id,
    p_zone_id: input.zone_id,
    p_section_id: input.section_id,
    p_location_id: input.location_id,
    p_copies: input.copies,
    p_reason: input.reason,
    p_note: note === '' ? null : note,
    p_origin: input.origin,
    p_client_uuid: input.client_uuid,
    p_printed_at: input.printed_at ?? null,
    p_label_expiry: input.label_expiry_date ?? null,
    // `undefined` = no ho dic; `null` = l'etiqueta no portava caducitat.
    p_label_expiry_given: input.label_expiry_date !== undefined,
  };
}

/** Una fila de l'historial d'un lot (`lot_label_reprint_history`): qui, quan, quantes còpies, el motiu i el que deia l'etiqueta. */
export const LotLabelReprintRow = z
  .object({
    id: uuid,
    printed_at: timestamptz,
    copies: z.number().int(),
    reason: z.enum(LOT_LABEL_REPRINT_REASONS),
    note: z.string().nullable(),
    origin: z.enum(LOT_LABEL_REPRINT_ORIGINS),
    lot_number: nonEmptyString,
    /** La caducitat que portava l'etiqueta. */
    expiry_date: dateString.nullable(),
    /** La caducitat que el lot tenia quan es va registrar (si difereix d'`expiry_date`, l'etiqueta no deia el que deia el lot). */
    lot_expiry_date: dateString.nullable(),
    zone_id: uuid.nullable(),
    section_id: uuid.nullable(),
    location_id: uuid.nullable(),
    printed_by: uuid,
    printed_by_name: z.string().nullable(),
  })
  .strict();
export type LotLabelReprintRow = z.infer<typeof LotLabelReprintRow>;

/** Els arguments de l'RPC `lot_label_reprint_history`: sempre d'UN lot. */
export function lotLabelReprintHistoryArgs(lot: {
  readonly supplierLotId: string | null;
  readonly finishedLotId: string | null;
  readonly limit?: number;
}): Record<string, unknown> {
  return {
    p_supplier_lot_id: lot.supplierLotId,
    p_finished_lot_id: lot.finishedLotId,
    p_limit: lot.limit ?? 50,
  };
}

/** Una línia (lot + lloc) amb existències, per a la cerca de lots de l'App (`stock_lots_for_labels`). */
export const StockLabelLotRow = z
  .object({
    lot_kind: z.enum(['supplier', 'finished']),
    lot_id: uuid,
    article_id: uuid,
    article_name: z.string(),
    article_sku: z.string(),
    lot_number: nonEmptyString,
    expiry_date: dateString.nullable(),
    zone_id: uuid.nullable(),
    section_id: uuid.nullable(),
    location_id: uuid.nullable(),
    /** El lloc ja escrit («Magatzem › Refrigerat › R-01»); null = sense lloc. */
    place_text: z.string().nullable(),
    /** Existències de la línia, en unitat base. PostgREST pot tornar un `numeric` com a text. */
    quantity: z.union([z.number(), z.string()]),
    unit_code: z.string().nullable(),
  })
  .strict();
export type StockLabelLotRow = z.infer<typeof StockLabelLotRow>;
